import React, { useState, useEffect } from 'react';
import { Risk } from '../types';
import { BowtieDiagram } from './bowtie.types';
import { BowtieCanvas } from './BowtieCanvas';
import { riskService } from '../riskService';
import { RefreshCw, AlertCircle, ArrowLeft } from 'lucide-react';
import { MODULE_META_CATALOG, setPageMeta } from '../../../../shared/meta';

interface StandaloneBowtiePageProps {
  initialRiskId?: string;
  onReturnToPlatform?: () => void;
}

export const StandaloneBowtiePage: React.FC<StandaloneBowtiePageProps> = ({
  initialRiskId,
  onReturnToPlatform,
}) => {
  const [risks, setRisks] = useState<Risk[]>([]);
  const [selectedRiskId, setSelectedRiskId] = useState<string>(initialRiskId || '');
  const [diagram, setDiagram] = useState<BowtieDiagram | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load all risks for dropdown
  useEffect(() => {
    riskService
      .getRisks({ limit: 100 })
      .then((res) => {
        setRisks(res.items || []);
        if (!selectedRiskId && res.items && res.items.length > 0) {
          setSelectedRiskId(initialRiskId || res.items[0].id);
        }
      })
      .catch((err) => console.error('Failed to load risks list', err));
  }, []);

  const loadDiagram = async (id: string) => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const data = await riskService.getBowtieDiagram(id);
      setDiagram(data);
    } catch (err: any) {
      console.error('Failed to load Bowtie diagram:', err);
      setError(err.message || 'Failed to load Bowtie diagram');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedRiskId) {
      loadDiagram(selectedRiskId);
    }
  }, [selectedRiskId]);

  useEffect(() => {
    if (diagram) {
      setPageMeta({
        title: `${diagram.riskNumber} — Bowtie Visual Risk Model`,
        desc: `ICAO Doc 9859 compliant visual safety barrier model, threats and consequence analysis for risk scenario ${diagram.title}.`,
        keywords: `bowtie, ${diagram.riskNumber}, ${diagram.tolerability}, risk analysis, safety barrier, luminix`,
      });
    } else {
      setPageMeta(MODULE_META_CATALOG['bowtie']);
    }
  }, [diagram]);

  return (
    <div style={{ width: '100vw', height: '100vh', display: 'flex', flexDirection: 'column', background: '#f8fafc', overflow: 'hidden' }}>
      {/* Top Header Bar */}
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 16px',
          background: '#ffffff',
          borderBottom: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
          zIndex: 10,
          flexShrink: 0,
          flexWrap: 'wrap',
          gap: '10px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          {onReturnToPlatform && (
            <button
              onClick={onReturnToPlatform}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                background: '#f8fafc',
                fontSize: '12px',
                fontWeight: 600,
                color: '#475569',
                cursor: 'pointer',
              }}
              title="Return to Luminix Platform"
            >
              <ArrowLeft size={14} />
              Return to Platform
            </button>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <img src="/logo.png" alt="Luminix Logo" style={{ width: '28px', height: '28px', objectFit: 'contain', borderRadius: '6px' }} />
            <div>
              <div style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>
                Bowtie Workspace
              </div>
              <div style={{ fontSize: '10px', color: '#64748b' }}>
                ICAO Doc 9859 Safety Barriers
              </div>
            </div>
          </div>
        </div>

        {/* Risk Selector */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: '220px', maxWidth: '650px', justifyContent: 'center' }}>
          <label style={{ fontSize: '12px', fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>
            Risk:
          </label>
          <select
            value={selectedRiskId}
            onChange={(e) => setSelectedRiskId(e.target.value)}
            style={{
              padding: '6px 10px',
              borderRadius: '8px',
              border: '1px solid #cbd5e1',
              fontSize: '12px',
              fontWeight: 500,
              background: '#ffffff',
              color: '#0f172a',
              flex: 1,
              minWidth: 0,
            }}
          >
            {risks.map((r) => (
              <option key={r.id} value={r.id}>
                {r.riskNumber} — {r.title} ({r.initialRiskIndex})
              </option>
            ))}
          </select>

          <button
            onClick={() => loadDiagram(selectedRiskId)}
            disabled={loading || !selectedRiskId}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 10px',
              borderRadius: '8px',
              border: '1px solid #cbd5e1',
              background: '#ffffff',
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
              color: '#475569',
              flexShrink: 0,
            }}
            title="Refresh"
          >
            <RefreshCw size={14} className={loading ? 'spin' : ''} />
          </button>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '11px', background: '#ecfdf5', color: '#047857', fontWeight: 600, padding: '3px 8px', borderRadius: '10px', border: '1px solid #a7f3d0' }}>
            ● Live
          </span>
        </div>
      </header>

      {/* Main Workspace Area */}
      <main style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        {loading && !diagram ? (
          <div style={{ height: '100%', background: '#f8fafc' }} />
        ) : error ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#dc2626' }}>
            <AlertCircle size={40} style={{ marginBottom: '12px' }} />
            <div style={{ fontSize: '16px', fontWeight: 700 }}>Failed to Load Bowtie Diagram</div>
            <p style={{ fontSize: '13px', color: '#64748b', marginTop: '6px' }}>{error}</p>
          </div>
        ) : diagram ? (
          <BowtieCanvas
            diagram={diagram}
            onDiagramUpdated={(updated) => setDiagram(updated)}
            height="100%"
          />
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#64748b' }}>
            No risks found to display.
          </div>
        )}
      </main>
    </div>
  );
};
